import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { MapPin, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";

const SLOTS = [
  "11:00 AM – 1:00 PM",
  "1:00 PM – 4:00 PM",
  "4:00 PM – 7:00 PM",
  "7:00 PM – 9:00 PM",
];

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    email: "",
    pickup_date: todayPlus(1),
    pickup_slot: SLOTS[0],
    notes: "",
  });

  if (items.length === 0) {
    return (
      <div className="px-6 py-24 text-center max-w-md mx-auto" data-testid="checkout-empty">
        <h1 className="font-serif-display text-4xl">Your cart is empty</h1>
        <p className="mt-3 text-[var(--srishti-text-secondary)]">
          Add a few sacred objects before reserving a pickup.
        </p>
        <Link to="/shop" className="btn-maroon mt-6 inline-flex">
          Browse the Shop
        </Link>
      </div>
    );
  }

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/orders", { ...form, items });
      clear();
      navigate(`/order/${res.data.id}`);
    } catch (err) {
      toast.error("Could not place reservation. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 md:py-16 fade-up" data-testid="checkout-page">
      <div className="max-w-[1100px] mx-auto">
        <span className="eyebrow">Enquiry</span>
        <h1 className="font-serif-display text-4xl md:text-5xl mt-2">
          Submit your enquiry
        </h1>
        <p className="mt-3 text-[var(--srishti-text-secondary)] max-w-xl">
          We&apos;ll call you back with prices, availability and your pickup slot at TC Palya.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-10">
          <form onSubmit={submit} className="md:col-span-3 space-y-6" data-testid="checkout-form">
            <section>
              <h2 className="font-serif-display text-2xl mb-4">Your details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full name" required>
                  <input
                    type="text"
                    value={form.customer_name}
                    onChange={handle("customer_name")}
                    className="srishti-input"
                    required
                    data-testid="checkout-name-input"
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={handle("phone")}
                    className="srishti-input"
                    required
                    data-testid="checkout-phone-input"
                  />
                </Field>
                <Field label="Email (optional)" className="md:col-span-2">
                  <input
                    type="email"
                    value={form.email}
                    onChange={handle("email")}
                    className="srishti-input"
                    data-testid="checkout-email-input"
                  />
                </Field>
              </div>
            </section>

            <section>
              <h2 className="font-serif-display text-2xl mb-4">Pickup slot</h2>
              <div className="bg-[var(--srishti-bg-alt)] border border-[var(--srishti-border)] p-4 mb-4 flex gap-3 text-sm">
                <MapPin size={18} className="text-[var(--srishti-saffron)] mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-[var(--srishti-text)]">
                    Srishti Gift &amp; Religious Articles Shop
                  </p>
                  <p className="text-[var(--srishti-text-secondary)]">
                    TC Palya, Bangalore · Mon–Sun 9:00 AM – 9:00 PM
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Date">
                  <div className="relative">
                    <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--srishti-text-secondary)]" />
                    <input
                      type="date"
                      min={todayPlus(0)}
                      value={form.pickup_date}
                      onChange={handle("pickup_date")}
                      className="srishti-input pl-9"
                      data-testid="checkout-date-input"
                    />
                  </div>
                </Field>
                <Field label="Time slot">
                  <div className="relative">
                    <Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--srishti-text-secondary)]" />
                    <select
                      value={form.pickup_slot}
                      onChange={handle("pickup_slot")}
                      className="srishti-input pl-9 appearance-none"
                      data-testid="checkout-slot-select"
                    >
                      {SLOTS.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </Field>
              </div>
            </section>

            <section>
              <Field label="Notes (gift wrapping, ritual queries…)">
                <textarea
                  rows={4}
                  value={form.notes}
                  onChange={handle("notes")}
                  className="srishti-input"
                  data-testid="checkout-notes-input"
                />
              </Field>
            </section>

            <button
              type="submit"
              className="btn-maroon w-full"
              disabled={submitting}
              data-testid="place-order-button"
            >
              {submitting ? "Submitting…" : "Submit Enquiry"}
            </button>
          </form>

          <aside className="md:col-span-2">
            <div className="bg-[var(--srishti-bg-alt)] border border-[var(--srishti-border)] p-6 sticky top-24">
              <h3 className="font-serif-display text-2xl mb-4">Order summary</h3>
              <ul className="divide-y divide-[var(--srishti-border)]">
                {items.map((it) => (
                  <li key={it.product_id} className="flex gap-3 py-3">
                    <img src={it.image} alt={it.name} className="w-14 h-16 object-cover" />
                    <div className="flex-1 text-sm">
                      <p className="font-serif-display text-base leading-tight">{it.name}</p>
                      <p className="text-[var(--srishti-text-secondary)] text-xs mt-1">
                        Qty {it.quantity} · Price on enquiry
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-[var(--srishti-border)]">
                <p className="eyebrow">Pricing</p>
                <p className="text-sm text-[var(--srishti-text-secondary)] leading-relaxed mt-2">
                  Final pricing will be shared by our team when we call to confirm your enquiry. Cash payment at our boutique on pickup.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
      <style>{`
        .srishti-input {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid var(--srishti-border);
          padding: 0.75rem 0.95rem;
          font-size: 0.95rem;
          color: var(--srishti-text);
          transition: border-color 0.2s ease;
          font-family: 'Manrope', sans-serif;
        }
        .srishti-input:focus { outline: none; border-color: var(--srishti-gold); }
      `}</style>
    </div>
  );
}

const Field = ({ label, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs uppercase tracking-widest font-semibold text-[var(--srishti-text-secondary)] mb-2">
      {label} {required && <span className="text-[var(--srishti-saffron)]">*</span>}
    </span>
    {children}
  </label>
);
